import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Row, Form, Tabs, Tab } from "react-bootstrap";
import styles from "./ClientEditor.module.css";
import Select, { SingleValue } from "react-select";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import {
  fetchClientData,
  fetchFormData,
  updateClientData,
} from "./ClientEditor.service";
import {
  loadClientForms,
  prefillRemainingTabsFromClientDetails,
  patchFieldsWithClientData,
  DirtyApi,
} from "../ClientCreator/ClientCreator.utils";
import { ClientEditorProps, handleClose } from "./ClientEditor.utils";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import { FormRecord } from "../ClientCreator/ClientCreator.utils";
import CygnusForm from "../../../Reusables/components/CygnusForm/CygnusForm";
import { ThreeCircles } from "react-loader-spinner";
import DocumentUploader from "../ClientCreator/components/DocumentUploader/DocumentUploader";
import { resolvePurposeFromFields } from "../ClientsGrid/ClientsGrid.utils";
import {
  getFormFlowByPurpose,
  getFormFlowForFields,
} from "../ClientsGrid/ClientsGrid.utils";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";

import * as Icon from "react-bootstrap-icons";
import ApprovalSettingsModal from "../ApproverSettingModal/ApproverSettingModal";
import { sendForApproval } from "../ClientsGrid/ClientsGrid.service";
import { getApproversList } from "../ClientCreator/ClientCreator.service";
import type { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import { COI_REGISTRY_PRIVILEGE_CODE } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import { fetchRuleApproverPrivileges } from "../../firm_setup/RulesModal/RulesModal.service";
import MatchedSSNEINClients from "../ClientCreator/components/MatchedSSNEINClients/MatchedSSNEINClients";
const isTrue = (v: any) => v === true || v === "true";

export const ClientEditor: React.FC<ClientEditorProps> = ({
  show,
  clientData,
  clientMasterId,
  disableEdit = false,
  allowApprovedEdit = false,
  onHide,
  refresh,
}) => {
  const purposes = [
    "tax",
    "client accounting services",
    "advisory",
    "business management",
    "assurance",
    "gyt",
    "tax & acct clients",
  ];

  const initialPurpose = useMemo(() => {
    if (
      clientData?.BusinessUnit &&
      purposes.includes(clientData.BusinessUnit.toLowerCase())
    ) {
      console.log("Initial purpose guess from BusinessUnit:", clientData?.BusinessUnit);
      return clientData?.BusinessUnit.toLowerCase();
    }
    return "tax";
  }, [clientData]);

  const [formFlowConfig, setFormFlowConfig] = useState(() =>
    getFormFlowByPurpose(initialPurpose as any)
  );
  const [showAlert, setShowAlert] = useState(false);

  type TabKey = (typeof formFlowConfig)[number]["key"];

  const orderedFlow = [...formFlowConfig]
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);

  const [activeTab, setActiveTab] = useState<TabKey>(
    formFlowConfig[0]?.key ?? "onboarding"
  );

  const createInitialTabState = <T,>(initialValue: T): Record<string, T> => {
    return (formFlowConfig || []).reduce((acc, step) => {
      acc[step.key] = initialValue;
      return acc;
    }, {} as Record<string, T>);
  };

  const [formRecordsByTab, setFormRecordsByTab] = useState(
    createInitialTabState<FormRecord[]>([])
  );
  const [selectedFormByTab, setSelectedFormByTab] = useState(
    createInitialTabState<FormRecord | null>(null)
  );
  const [formFieldsByTab, setFormFieldsByTab] = useState(
    createInitialTabState<FieldProps[]>([])
  );

  const [isFormValid, setIsFormValid] = useState(false);

  const [clientDetails, setClientDetails] = useState<any>();
  const [fetchClientApiProgress, setFetchClientApiProgress] = useState(false);
  const [clientUpdateApiProgress, setClientUpdateApiProgress] = useState(false);
  const [formsApiProgress, setFormsApiProgress] = useState(false);
  const [approvalApiProgress, setApproalApiProgress] = useState<boolean>(false);
  const [approvalSettingModal, setApprovalSettingModal] = useState(false);
  const [clientTypeCode, setClientTypeCode] = useState<string>("");
  const [firstApprover, setFirstApprover] = useState<Approver | null>(null);
  const [fetchApproversProgress, setFetchApproversProgress] = useState(false);
  const [coiApproverGroupLabel, setCoiApproverGroupLabel] = useState<string>();
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const dirtyApiRef = useRef<DirtyApi | null>(null);
  // Document validation state
  const [documentsValid, setDocumentsValid] = useState<boolean>(true);
  const [missingDocuments, setMissingDocuments] = useState<string[]>([]);
  const [showDocumentWarning, setShowDocumentWarning] = useState<boolean>(false);


  const [showSSNEINModal, setShowSSNEINModal] = useState(false);
  const [clientsMatchMessage, setClientsMatchMessage] = useState(null);
  const [clientsMatchedData, setClientsMatchedData] = useState([]);
  const [continueSavingClient, setContinueSavingClient] = useState(false);
  const [showMatchedSSNEINModal, setShowMatchedSSNEINModal] = useState(false);

  const handleDocumentValidationChange = (result: {
    isValid: boolean;
    missing: string[];
  }) => {
    setDocumentsValid(result.isValid);
    setMissingDocuments(result.missing);
  };

  const purposeLabel = React.useMemo(() => {
    const allFields = Object.values(formFieldsByTab).flat();
    return resolvePurposeFromFields(allFields) || initialPurpose || "";
  }, [formFieldsByTab, initialPurpose]);

  const [enabledTabs, setEnabledTabs] = useState<TabKey[]>(
    formFlowConfig.map((tab) => tab.key as TabKey)
  );

  const normalizeComparableValue = (v: unknown): string => {
    if (v === null || typeof v === "undefined") return "";

    if (typeof v === "boolean") return v ? "true" : "false";

    if (typeof v === "number") return String(v);

    if (typeof v === "string") {
      const s = v.trim();
      const lower = s.toLowerCase();

      if (lower === "true") return "true";
      if (lower === "false") return "false";

      // unify numeric strings: "25" and 25
      if (/^-?\d+(\.\d+)?$/.test(s)) return String(Number(s));

      return s;
    }

    // arrays/objects: stabilize into a string
    if (Array.isArray(v)) {
      return JSON.stringify(v.map((x) => normalizeComparableValue(x)));
    }

    if (typeof v === "object") {
      return JSON.stringify(v);
    }

    return String(v);
  };

  const normalizeByType = (type: string, value: unknown): string => {
    if (
      (type === "checkbox" || type === "radio") &&
      (value === "" || value === null || typeof value === "undefined")
    ) {
      return "false";
    }
    return normalizeComparableValue(value);
  };

  const buildFieldsFingerprint = (fields: FieldProps[]) => {
    // Only include what represents "user data" changes.
    return JSON.stringify(
      (fields || [])
        .map((f) => ({
          id: f.id,
          key: (f as any).allfields_key ?? "",
          code: (f as any).allfields_code ?? "",
          type: f.type,
          value: normalizeByType(f.type, (f as any).value),
        }))
        .sort((a, b) => {
          const ak = `${a.key}|${a.code}|${a.id}`;
          const bk = `${b.key}|${b.code}|${b.id}`;
          return ak.localeCompare(bk);
        })
    );
  };

  const createInitialTabStringState = (
    initialValue: string
  ): Record<string, string> => {
    return (formFlowConfig || []).reduce((acc, step) => {
      acc[step.key] = initialValue;
      return acc;
    }, {} as Record<string, string>);
  };

  const createInitialTabBoolState = (
    initialValue: boolean
  ): Record<string, boolean> => {
    return (formFlowConfig || []).reduce((acc, step) => {
      acc[step.key] = initialValue;
      return acc;
    }, {} as Record<string, boolean>);
  };

  const [baselineByTab, setBaselineByTab] = useState<Record<string, string>>(
    createInitialTabStringState("")
  );

  const [baselineReadyByTab, setBaselineReadyByTab] = useState<
    Record<string, boolean>
  >(createInitialTabBoolState(false));

  const [dirtyByTab, setDirtyByTab] = useState<Record<string, boolean>>(
    createInitialTabBoolState(false)
  );

  const shouldDisableEdit = useMemo(() => {
    const status = clientDetails?.ApprovalStatus;
    const isApprovedOrPartial =
      status === "Approved" || status === "Partially Approved";

    // Respect the prop OR the fresh status from DB
    return disableEdit || (isApprovedOrPartial && !allowApprovedEdit);
  }, [disableEdit, allowApprovedEdit, clientDetails]);

  const enableNextTab = async (
    currentTab: TabKey,
    activate: boolean = true
  ) => {
    const idx = orderedFlow.findIndex((t) => t.key === currentTab);
    const next = orderedFlow[idx + 1];
    if (!next) return;

    // Enable the next tab (if not already)
    setEnabledTabs((prev) =>
      prev.includes(next.key) ? prev : [...prev, next.key]
    );

    // Immediately switch to it (programmatic switch ignores the disabled prop)
    if (activate) setActiveTab(next.key as TabKey);

    if (clientMasterId) {
      await prefillRemainingTabsFromClientDetails(
        clientMasterId,
        activeTab,
        formFieldsByTab,
        selectedFormByTab,
        setFormFieldsByTab,
        setClientDetails,
        setFetchClientApiProgress
      );
    }
  };

  // debounce bucket
  const fieldEventTimerRef = React.useRef<number | undefined>(undefined);

  // 1) event routing
  const fieldEventHandlers: Record<
    string,
    (args: { value: any; field: FieldProps }) => Promise<void> | void
  > = {
    // inside ClientCreator fieldEventHandlers
    "client.prefill": async ({ value }) => {
      if (!value) return;

      if (fieldEventTimerRef.current) clearTimeout(fieldEventTimerRef.current);

      fieldEventTimerRef.current = window.setTimeout(async () => {
        setFormsApiProgress(true);
        try {
          const res = await fetchClientData(
            value,
            (searched) => setClientDetails((prev: any) => ({
              ...searched,
              ApprovalStatus: prev?.ApprovalStatus,
            })),
            setFormsApiProgress,
            true
          );

          if (!res?.success) {
            // API already gave you the message
            setLookupError(
              res?.message || `Client Id: ${value} does not exist`
            );
            setShowLookupModal(true);
            return;
          }

          // success path: res.data[0] was set into clientDetails already
          const details = Array.isArray(res.data) ? res.data[0] : undefined;
          if (!details) return;

          const dirtyIds = dirtyApiRef.current?.get() ?? [];
          setFormFieldsByTab((prev) => {
            const current = prev[activeTab] ?? [];
            const patched = patchFieldsWithClientData(current, details, {
              skip: { byId: dirtyIds, byKey: ["business_unit"] },
            });
            return { ...prev, [activeTab]: patched };
          });
        } finally {
        }
      }, 150);
    },
  };

  // 2) single entry point from CygnusForm
  const handleFieldEventFromForm = ({
    eventKey,
    value,
    field,
  }: {
    eventKey?: string;
    value: any;
    field: FieldProps;
  }) => {
    const key = eventKey ?? "default";
    const handler = fieldEventHandlers[key];
    if (handler) handler({ value, field });
  };

  // whenever flow shape changes, re-sync per-tab maps & active tab
  useEffect(() => {
    setFormRecordsByTab((prev) => {
      const next: Record<string, FormRecord[]> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? [];
      });
      return next;
    });

    setSelectedFormByTab((prev) => {
      const next: Record<string, FormRecord | null> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? null;
      });
      return next;
    });

    setFormFieldsByTab((prev) => {
      const next: Record<string, FieldProps[]> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? [];
      });
      return next;
    });

    setBaselineByTab((prev) => {
      const next: Record<string, string> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? "";
      });
      return next;
    });

    setBaselineReadyByTab((prev) => {
      const next: Record<string, boolean> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? false;
      });
      return next;
    });

    setDirtyByTab((prev) => {
      const next: Record<string, boolean> = {};
      formFlowConfig.forEach((s) => {
        next[s.key] = prev[s.key] ?? false;
      });
      return next;
    });

    if (!formFlowConfig.find((s) => s.key === activeTab)) {
      const first = formFlowConfig[0]?.key;
      if (first) setActiveTab(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFlowConfig]);

  useEffect(() => {
    if (!show) return;

    setShowAlert(false);
    setBaselineByTab(createInitialTabStringState(""));
    setBaselineReadyByTab(createInitialTabBoolState(false));
    setDirtyByTab(createInitialTabBoolState(false));
    dirtyApiRef.current?.clear?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Helper: resolve configured FormId field for a given tab
  const getFormIdForTab = (tab: TabKey) => {
    const idField = formFlowConfig.find((s) => s.key === tab)?.idField;
    return idField ? clientData?.[idField] : undefined;
  };

  const hasTruthyId = (v: any) =>
    !(v === undefined || v === null || v === "" || v === 0 || v === "0");

  const preselectedForActiveTab = useMemo(() => {
    const id = getFormIdForTab(activeTab);
    // true if there is a usable id OR we've already loaded & chosen a form for this tab
    return hasTruthyId(id) || !!selectedFormByTab[activeTab];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, clientData, selectedFormByTab, formFlowConfig]);

  const getClientTypeCodeFromFields = (): string => {
    const fields = formFieldsByTab[activeTab] ?? [];

    const f = fields.find((ff) => {
      const code = String(
        ff.allfields_code ?? ff.allfields_key ?? ff.label ?? ""
      ).toLowerCase();

      return code === "clienttypecode";
    });

    return String(f?.value ?? "").trim();
  };

  const refreshApproverBadge = async () => {
    if (!clientMasterId) {
      setFirstApprover(null);
      return;
    }

    setFetchApproversProgress(true);
    try {
      const ctc = getClientTypeCodeFromFields();
      const list = await getApproversList(clientMasterId, ctc);

      const first = list[0];
      const display =
        first?.COIApprover === "Y"
          ? coiApproverGroupLabel || "Reviewers"
          : first?.FirstName || first?.LastName
          ? `${first?.FirstName ?? ""} ${first?.LastName ?? ""}`.trim()
          : first?.UserName ?? first?.Email ?? "";

      const approvalOff =
        String(first?.ApprovalRequired ?? "").toUpperCase() === "N";

      setFirstApprover(
        approvalOff || !first
          ? null
          : { ...first, Displayname: display || "Not set" }
      );
    } finally {
      setFetchApproversProgress(false);
    }
  };

  const openApprovalSettings = () => {
    const ctc = getClientTypeCodeFromFields();
    setClientTypeCode(ctc);
    setApprovalSettingModal(true);
  };

  useEffect(() => {
    fetchRuleApproverPrivileges().then((options) => {
      const coiOption = options.find((o) => o.value === COI_REGISTRY_PRIVILEGE_CODE);
      if (coiOption) setCoiApproverGroupLabel(coiOption.label);
    });
  }, []);

  useEffect(() => {
    if (clientMasterId) refreshApproverBadge();
    else setFirstApprover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMasterId, activeTab, coiApproverGroupLabel]);

  useEffect(() => {
    const arr = formFieldsByTab[activeTab] ?? [];
    const r = arr.find(
      (f) => f.allfields_key === "Range Of Fees" || f.label === "Range Of Fees"
    );
    if (r) {
      console.log(`[${activeTab}] Range Of Fees =`, r.value, {
        from: "formFieldsByTab effect",
        stack: new Error().stack?.split("\n").slice(0, 3).join("\n"),
      });
    }
  }, [formFieldsByTab, activeTab]);

  // Load records + client details on open
  useEffect(() => {
    if (!show) return;

    if (clientMasterId) {
      fetchClientData(clientMasterId, setClientDetails, () => {});
    }

    const allHaveIds = formFlowConfig.every((step) => !!clientData?.[step.idField]);

    if (allHaveIds) {
      formFlowConfig.forEach((step) => {
        const id = clientData?.[step.idField];
        if (id) {
          fetchFormData(
            id,
            (details) => {
              setSelectedFormByTab((prev) => ({
                ...prev,
                [step.key]: details,
              }));
            },
            () => {}
          );
        }
      });
    } else {
      // Otherwise, load selectable forms for each step (key-based filtering)
      loadClientForms(formFlowConfig, setFormRecordsByTab, setFormsApiProgress);
    }
  }, [show, clientData, clientMasterId, formFlowConfig]);

  // Auto-select a form per tab (if not preselected by id)
  useEffect(() => {
    if (!show) return;

    formFlowConfig.forEach(({ key }) => {
      const records = formRecordsByTab[key];
      if (!records?.length || selectedFormByTab[key]) return;

      const formId = getFormIdForTab(key);
      const parsedFormId =
        typeof formId === "string" ? parseInt(formId, 10) : formId;

      const match = records.find((r) => r.FirmFormId === parsedFormId);
      const fallback = records[0];
      const selected = match || fallback;

      if (selected) {
        setSelectedFormByTab((prev) => ({ ...prev, [key]: selected }));
      }
    });
  }, [show, formRecordsByTab, formFlowConfig, selectedFormByTab]);

  const currentSelectedFormId = selectedFormByTab[activeTab]?.FirmFormId ?? null;


  useEffect(() => {
    if (!show) return;

    setBaselineByTab((prev) => ({ ...prev, [activeTab]: "" }));
    setBaselineReadyByTab((prev) => ({ ...prev, [activeTab]: false }));
    setDirtyByTab((prev) => ({ ...prev, [activeTab]: false }));
  }, [show, activeTab, clientMasterId, currentSelectedFormId]);

  useEffect(() => {
    if (!show) return;

    const currentForm = selectedFormByTab[activeTab];
    if (!currentForm?.InputStructure?.fields) return;

    if (clientMasterId && typeof clientDetails === "undefined") return;

    const existingFields = formFieldsByTab[activeTab] ?? [];
    const existingMap = new Map(existingFields.map((f) => [f.id, f]));
    const preserveExistingValues = baselineReadyByTab[activeTab];

    const filled = currentForm.InputStructure.fields.map((field: FieldProps) => {
      const existing = existingMap.get(field.id)?.value;

      return {
        ...field,
        value:
          preserveExistingValues &&
          existing !== undefined &&
          existing !== null &&
          existing !== ""
            ? existing
            : field.allfields_code
              ? clientDetails?.[field.allfields_code] ?? ""
              : "",
        disabled: shouldDisableEdit,
      };
    });

    const filledFp = buildFieldsFingerprint(filled);

    // console.log("HYDRATE TAB", {
    //   activeTab,
    //   baselineReady: baselineReadyByTab[activeTab],
    //   filledFp,
    //   clientDetailsLoaded: typeof clientDetails !== "undefined",
    // });

    setFormFieldsByTab((prev) => ({ ...prev, [activeTab]: filled }));

    if (!baselineReadyByTab[activeTab]) {
      setBaselineByTab((prev) => ({ ...prev, [activeTab]: filledFp }));
      setBaselineReadyByTab((prev) => ({ ...prev, [activeTab]: true }));
      setDirtyByTab((prev) => ({ ...prev, [activeTab]: false }));

      // console.log("BASELINE INITIALIZED FROM HYDRATED FIELDS", {
      //   activeTab,
      //   filledFp,
      // });
    }
  }, [
    show,
    activeTab,
    clientDetails,
    clientMasterId,
    selectedFormByTab,
    shouldDisableEdit,
    baselineReadyByTab,
  ]);

  useEffect(() => {
    const allFields = Object.values(formFieldsByTab).flat();
    if (!allFields.length) return;

    const refined = getFormFlowForFields(allFields);
    if (!refined.length) return;

    const keysNow = formFlowConfig.map((s) => s.key).join("|");
    const keysNew = refined.map((s) => s.key).join("|");

    if (keysNow !== keysNew) {
      setFormFlowConfig(refined);
    }
  }, [formFieldsByTab, formFlowConfig]);

  // Tab switch logic
  const handleTabSelect = async (key: any) => {
    if (!key) return;
    if (enabledTabs.includes(key)) {
      setActiveTab(key as TabKey);
      if (clientMasterId) {
        await prefillRemainingTabsFromClientDetails(
          clientMasterId,
          activeTab,
          formFieldsByTab,
          selectedFormByTab,
          setFormFieldsByTab,
          setClientDetails,
          setFetchClientApiProgress
        );
      }
    }
  };

  // Dropdown select a form variant
  const handleFormSelect = (
    selected: SingleValue<{ value: number; label: string }>
  ) => {
    if (!selected) return;
    const pool = formRecordsByTab[activeTab] || [];
    const selectedForm = pool.find((r) => r.FirmFormId === selected.value);
    if (selectedForm) {
      setSelectedFormByTab((prev) => ({ ...prev, [activeTab]: selectedForm }));
    }
  };

  const updateFormFields = (fields: FieldProps[]) => {
    setFormFieldsByTab((prev) => ({ ...prev, [activeTab]: fields }));

    const nextFp = buildFieldsFingerprint(fields);

    if (!baselineReadyByTab[activeTab]) {
      setBaselineByTab((prev) => ({ ...prev, [activeTab]: nextFp }));
      setBaselineReadyByTab((prev) => ({ ...prev, [activeTab]: true }));
      setDirtyByTab((prev) => ({ ...prev, [activeTab]: false }));
      return;
    }

    setBaselineByTab((basePrev) => {
      const baseFp = basePrev[activeTab] ?? "";
      const isDirty = baseFp !== "" && nextFp !== baseFp;

      setDirtyByTab((dirtyPrev) => ({ ...dirtyPrev, [activeTab]: isDirty }));
      return basePrev;
    });
  };

  // useEffect(() => {
  //   if (!dirtyByTab[activeTab]) return;

  //   console.log("DIRTY TAB:", activeTab, {
  //     base: baselineByTab[activeTab],
  //     now: buildFieldsFingerprint(formFieldsByTab[activeTab] ?? []),
  //   });
  // }, [dirtyByTab, activeTab, baselineByTab, formFieldsByTab]);

  const handleSaveAndNext = async () => {
    const currentFields = formFieldsByTab[activeTab] ?? [];
    const currentFp = buildFieldsFingerprint(currentFields);
    const baselineFp = baselineByTab[activeTab] ?? "";
    const computedDirty = baselineFp !== "" && currentFp !== baselineFp;

    // console.log("NEXT CLICK DEBUG", {
    //   activeTab,
    //   baselineReady: baselineReadyByTab[activeTab],
    //   baselineFp,
    //   currentFp,
    //   computedDirty,
    //   same: baselineFp === currentFp,
    // });

    if (!computedDirty) {
      // console.log("SKIP SAVE -> moving to next tab");
      await enableNextTab(activeTab);
      return;
    }


    // console.log("SAVE -> updateClientData calleddd", continueSavingClient);
    setContinueSavingClient(true);

    const formUrl = selectedFormByTab[activeTab]?.Url ?? "";
    const formId = selectedFormByTab[activeTab]?.FirmFormId ?? 0;
    
    updateClientData(
      setClientUpdateApiProgress,
      currentFields,
      clientMasterId,
      formUrl,
      formId,
      clientData?.["SourceSystemCode"] ?? "",
      setShowSSNEINModal,
      setClientsMatchMessage,
      setClientsMatchedData,
      continueSavingClient,
      setShowMatchedSSNEINModal,
      async () => {
        // Save succeeded: reset baseline for this tab
        const savedFp = buildFieldsFingerprint(currentFields);

        // console.log("SAVE SUCCESS", {
        //   activeTab,
        //   savedFp,
        // });

        setBaselineByTab((prev) => ({ ...prev, [activeTab]: savedFp }));
        setBaselineReadyByTab((prev) => ({ ...prev, [activeTab]: true }));
        setDirtyByTab((prev) => ({ ...prev, [activeTab]: false }));

        // go next after save success
        await enableNextTab(activeTab);
      }
    );
  };

  const hasUnsavedChanges = useMemo(() => {
    // consider only enabled steps, and ignore DOCUMENTS
    const enabledKeys = formFlowConfig.filter(s => s.enabled).map(s => s.key);

    return enabledKeys.some((k) => {
      if (k === "DOCUMENTS") return false;
      return dirtyByTab[k] === true;
    });
  }, [dirtyByTab, formFlowConfig]);

  const handleAttemptClose = () => {
    const dirtyIds = dirtyApiRef.current?.get?.() ?? [];
    const hasRealUserChanges = dirtyIds.length > 0 || hasUnsavedChanges;

    if (hasRealUserChanges) setShowAlert(true);
    else {
      handleClose(
        setClientDetails,
        setFormFieldsByTab,
        setSelectedFormByTab,
        onHide
      );
    }
  };

  return (
    <>
      <AlertModal
        show={showAlert}
        onHide={() => setShowAlert(false)}
        title="Are you sure?"
        message="Closing this will discard all the unsaved data. Do you want to continue?"
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: () => setShowAlert(false) },
          {
            label: "Continue",
            onClick: () =>
              handleClose(
                setClientDetails,
                setFormFieldsByTab,
                setSelectedFormByTab,
                onHide
              ),
          },
        ]}
      />

      <AlertModal
        show={showLookupModal}
        onHide={() => setShowLookupModal(false)}
        title="Client not found"
        message={lookupError ?? "The client id you entered does not exist."}
        type="warning"
        listItems={[]}
      />

      <AlertModal
        show={showDocumentWarning}
        onHide={() => setShowDocumentWarning(false)}
        title="Missing Required Documents"
        message="Please upload the following required documents before sending for approval:"
        type="warning"
        listItems={missingDocuments}
      />

      {approvalSettingModal && (
        <ApprovalSettingsModal
          title="Approval Setting"
          clientMasterId={clientMasterId}
          clientTypeCode={clientTypeCode}
          show={approvalSettingModal}
          onHide={() => setApprovalSettingModal(false)}
          onUpdated={async () => {
            await refreshApproverBadge();
            setApprovalSettingModal(false);
          }}
          onApproversRefreshed={refreshApproverBadge}
          disableCheck={true}
        />
      )}

      <AlertModal
        show={showSSNEINModal}
        onHide={() => setShowSSNEINModal(false)}
        title="Matched entered SSN/EIN with Multiple Clients"
        message={`${clientsMatchMessage}`}
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: () => setShowSSNEINModal(false) },
          {
            label: "Continue", onClick: () => {              
              handleSaveAndNext()              
              setShowSSNEINModal(false)
            }
          },
        ]}
      />

      {showMatchedSSNEINModal && (
        <MatchedSSNEINClients
          show={showMatchedSSNEINModal}
          onHide={() => setShowMatchedSSNEINModal(false)}
          matchedClients={clientsMatchedData}
          setContinueSavingClient={setContinueSavingClient}
          handleSaveandNext={handleSaveAndNext}
        />
      )}

      <Modal
        show={show}
        onHide={handleAttemptClose}
        size="lg"
        backdrop="static"
        backdropClassName={styles.clientShellModalBackdrop}
        centered
        className={styles.editClientModal}
        keyboard={false}
      >
        <Modal.Header className={styles.editClientModalHeader} closeButton>
          <span className={styles.editClientModalHeaderTitle}>Edit Client</span>
        </Modal.Header>

        <Modal.Body className={styles.editClientModalBody}>
          {formsApiProgress && (
            <div className={styles.editorLoader}>
              <div className="flex-row-center-center" style={{ marginLeft: 5 }}>
                <ThreeCircles
                  height="50"
                  width="50"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
                <div style={{ marginLeft: 10 }}>Loading your form</div>
              </div>
            </div>
          )}

          <Row className={`no-gutter ${styles.editorMainButtons}`}>
            {purposeLabel ? (
              <div className={styles.losContainer}>
                <h5 className={styles.losBadge}>
                  <span className={styles.label}>Line of service:</span>
                  <span className={styles.value}>{purposeLabel}</span>
                </h5>
              </div>
            ) : null}

            {activeTab === "DOCUMENTS" && (
              <div className={styles.losContainer}>
                <h5 className={styles.losBadge}>
                  <span className={styles.label}>Approver:</span>
                  <span className={styles.value}>
                    {firstApprover?.Displayname
                      ? firstApprover.Displayname
                      : "Not set"}
                  </span>
                </h5>
              </div>
            )}

            {/* Only show form selector if this tab wasn’t preselected by id and isn’t documents */}
            {!preselectedForActiveTab &&
              activeTab !== "DOCUMENTS" &&
              !fetchClientApiProgress &&
              !formsApiProgress && (
                <Form.Group style={{ marginRight: 20, width: "fit-content" }}>
                  <Select
                    options={
                      formRecordsByTab[activeTab]?.map((f) => ({
                        value: f.FirmFormId,
                        label: f.FormName,
                      })) ?? []
                    }
                    value={
                      selectedFormByTab[activeTab]
                        ? {
                            value: selectedFormByTab[activeTab]!.FirmFormId,
                            label: selectedFormByTab[activeTab]!.FormName,
                          }
                        : null
                    }
                    onChange={handleFormSelect}
                    classNamePrefix="react-select"
                    isLoading={formsApiProgress}
                    placeholder="Select form"
                  />
                </Form.Group>
              )}

            {activeTab !== "DOCUMENTS" ? (
              <CustomButton
                progress={clientUpdateApiProgress}
                disabled={!isFormValid || formsApiProgress}
                onclick={
                  !shouldDisableEdit
                    ? handleSaveAndNext
                    : () => enableNextTab(activeTab)
                }
                content={"Next"}
              />
            ) : (
              <>
                <CustomButton
                  content={"Send for approval"}
                  progress={approvalApiProgress}
                  disabled={shouldDisableEdit || !clientMasterId}
                  onclick={async () => {
                    // Validate documents first
                    if (!documentsValid) {
                      setShowDocumentWarning(true);
                      return;
                    }

                    const approved = await sendForApproval(
                      [{ ClientMasterId: clientMasterId }],
                      setApproalApiProgress
                    );

                    if (approved) {
                      handleClose(
                        setClientDetails,
                        setFormFieldsByTab,
                        setSelectedFormByTab,
                        onHide
                      );
                      refresh();
                    }
                  }}
                />

                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={openApprovalSettings}
                  disabled={shouldDisableEdit || !clientMasterId}
                  title="Approval Setting"
                >
                  <Icon.Gear />
                </button>
              </>
            )}
          </Row>

          <div className={styles.tabsArea}>
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabSelect}
              className="mb-3"
            >
              {formFlowConfig
                .filter((step) => step.enabled)
                .sort((a, b) => a.order - b.order)
                .map((step) => (
                  <Tab
                    key={step.key}
                    eventKey={step.key}
                    title={step.label} // labels are cosmetic; rename freely
                    className={styles.formTab}
                  >
                    {activeTab === step.key && step.key === "DOCUMENTS" ? (
                      <DocumentUploader
                        fields={clientDetails}
                        clientMasterId={clientMasterId}
                        disabled={shouldDisableEdit}
                        onValidationChange={handleDocumentValidationChange}
                        formFieldsByTab={formFieldsByTab}
                      />
                    ) : (
                      <>
                        {activeTab === step.key &&
                          selectedFormByTab[step.key] && (
                            <CygnusForm
                              formId={`edit-form-${step.key}`}
                              fields={formFieldsByTab[step.key] ?? []}
                              onFieldsChange={updateFormFields}
                              onValidationChange={setIsFormValid}
                              onFieldEvent={handleFieldEventFromForm}
                              onDropdownsLoaded={() =>
                                setFormsApiProgress(false)
                              }
                              provideDirtyApi={(api) =>
                                (dirtyApiRef.current = api)
                              }
                              onDirtyChange={() => {}}
                              fieldVariant={selectedFormByTab[step.key]?.InputStructure?.formSettings?.fieldVariant}
                              sectionVariant={selectedFormByTab[step.key]?.InputStructure?.formSettings?.sectionVariant}
                              sectionLayout={selectedFormByTab[step.key]?.InputStructure?.formSettings?.sectionLayout}
                              intakeRules={selectedFormByTab[step.key]?.InputStructure?.formSettings?.intakeRules}
                            />
                          )}
                      </>
                    )}
                  </Tab>
                ))}
            </Tabs>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ClientEditor;
